import { DatePickerTriggerButton } from "$root/components/date-picker-popover";
import { useLocaleFormatters } from "$root/localization/hooks";
import { serverTodayAsLocalDate } from "$root/utils";
import { InfoTooltip } from "$root/widgets-architecture/layout/WidgetsMapper/InfoTooltip";
import { TooltipContent } from "@mdotm/shadcn/legacy/components";
import { FormField, Label } from "@mdotm/shadcn/mdotm/components";
import { useTranslation } from "react-i18next";
import { MarketDatePicker } from "./MarketDatePicker";
import { isMarketDateSelectable } from "./marketDate";
import type { MarketDateControl } from "./useMarketDate";

/**
 * The field label, followed in quantity mode by an info icon that says what the grid's prices are:
 * a past day's closes, or today's estimate from the latest close. In weight mode the day is only
 * what the save is dated to, so there is nothing to explain.
 */
function MarketDateLabel({ htmlFor, pricesNote }: { htmlFor: string; pricesNote: string | null }) {
	const { t } = useTranslation();
	return (
		<div className="flex items-center gap-1 mb-px">
			<Label htmlFor={htmlFor}>{t("EDIT_COMPOSITION.REFERENCE_DATE.LABEL")}</Label>
			{pricesNote && (
				// Zero-height, so the 20px icon overhangs the label's line instead of growing it: the header
				// row aligns its controls by their bottoms, and this label must stay level with its neighbours'.
				<span className="flex h-0 items-center" data-qualifier="CompositionEditor/MarketDate/PricesInfo">
					<InfoTooltip align="startToEnd">
						<TooltipContent>{pricesNote}</TooltipContent>
					</InfoTooltip>
				</span>
			)}
		</div>
	);
}

/**
 * The market date field in the composition editor's header controls (MDOT-25190): the label
 * over the date button that opens the picker. It sits beside the decimals stepper and the group-by
 * select, and takes their shape — label above, control below.
 *
 * The control prices *today's* holdings at the chosen day, where the portfolio history view's
 * as-of date rolls the holdings themselves back.
 *
 * The day and the way to change it come from the composition builder, which owns them
 * ({@link MarketDateControl}); this file only draws them. Both editing modes get the field: in
 * quantity mode the day is what the rows are priced at, in weight mode it is what the save is
 * dated to (MDOT-25192), so only quantity mode gets the label's prices note.
 */
export function MarketDateField({
	control,
	compositionMode,
}: {
	control: MarketDateControl;
	/** Required, `undefined` included, so the caller cannot drop it and silently lose the prices note. */
	compositionMode: "weight" | "quantity" | undefined;
}): React.JSX.Element {
	const { value: marketDate, lastTradeDate, repricing } = control;
	const { t } = useTranslation();
	const { formatDate } = useLocaleFormatters();

	// False means "there is no day but today to offer": the last trade is today, so the default is
	// already the only answer, or the portfolio has never traded and there is no lower bound to price
	// against.
	const selectable = isMarketDateSelectable({ lastTradeDate });
	const isToday = marketDate === serverTodayAsLocalDate();
	const label = isToday ? t("EDIT_COMPOSITION.REFERENCE_DATE.TODAY") : formatDate(marketDate);
	// "Today" says where the editor is, not what the button does — so with no date chosen the
	// accessible name says the latter. Once a day is chosen the day itself is the thing worth announcing.
	const ariaLabel = isToday
		? t("EDIT_COMPOSITION.REFERENCE_DATE.CHANGE_REFERENCE_DATE")
		: `${t("EDIT_COMPOSITION.REFERENCE_DATE.LABEL")}: ${label}`;
	const pricesNote =
		compositionMode !== "quantity"
			? null
			: isToday
				? t("EDIT_COMPOSITION.REFERENCE_DATE.PRICES_INFO.TODAY")
				: t("EDIT_COMPOSITION.REFERENCE_DATE.PRICES_INFO.PAST", { date: label });

	return (
		<FormField
			label={({ htmlFor }) => <MarketDateLabel htmlFor={htmlFor} pricesNote={pricesNote} />}
			data-qualifier="CompositionEditor/MarketDate"
		>
			{({ id }) => {
				const trigger = (
					<DatePickerTriggerButton
						id={id}
						label={label}
						ariaLabel={ariaLabel}
						disabled={!selectable || repricing}
						qualifier="CompositionEditor/MarketDate/Button"
					/>
				);
				// Greyed rather than hidden (AC-8): a control that is present and disabled says the date is
				// normally changeable, where a missing one says nothing at all. It is rendered outside the
				// picker in that state so there is nothing behind it to open — which is also what keeps a
				// second day from being picked while the first is being priced (MDOT-25191). The two reasons
				// stay separate: `selectable` is a property of the portfolio, `repricing` of this moment.
				return selectable && !repricing ? (
					<MarketDatePicker marketDate={marketDate} setMarketDate={control.set} lastTradeDate={lastTradeDate}>
						{trigger}
					</MarketDatePicker>
				) : (
					trigger
				);
			}}
		</FormField>
	);
}
